import Button from "@components/Button"
import Layout from "@components/Layout"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

interface ProgressStep {
  label: string
  durationMs: number
}

interface Props {
  steps: ProgressStep[]
  resultTitleKey: string
  resultTextKey: string
  buttonKey?: string
  hiddenInputName: string
}

const TICK_INTERVAL_MS = 50

export default function ProgressFeedbackPage({
  steps,
  resultTitleKey,
  resultTextKey,
  buttonKey,
  hiddenInputName,
}: Props) {
  const { t } = useTranslation()
  const submit = useSubmit()

  const totalDuration = steps.reduce((sum, s) => sum + s.durationMs, 0)

  const [elapsedMs, setElapsedMs] = useState(0)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (done) return

    const timer = setInterval(() => {
      setElapsedMs((prev) => {
        const next = prev + TICK_INTERVAL_MS
        if (next >= totalDuration) {
          clearInterval(timer)
          setDone(true)
          return totalDuration
        }
        return next
      })
    }, TICK_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [done, totalDuration])

  const progress = Math.min((elapsedMs / totalDuration) * 100, 100)

  const getCurrentStepLabel = (): string => {
    let accumulated = 0
    for (const step of steps) {
      accumulated += step.durationMs
      if (elapsedMs < accumulated) return step.label
    }
    return steps[steps.length - 1].label
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  const circumference = 2 * Math.PI * 54
  const strokeDashoffset = circumference - (progress / 100) * circumference

  return (
    <Layout>
      <div className="flex grow flex-col items-center justify-center px-4">
        {!done ? (
          <div className="flex flex-col items-center gap-8">
            <div className="relative">
              <svg width="140" height="140" className="-rotate-90">
                <circle
                  cx="70"
                  cy="70"
                  r="54"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="70"
                  cy="70"
                  r="54"
                  fill="none"
                  stroke="#7AF2A8"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-100 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-custom-charcoal text-2xl font-bold">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>

            <p className="text-center text-lg font-semibold text-gray-500">
              {getCurrentStepLabel()}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="text-7xl">🚀</div>

            <h1 className="custom-heading">{t(resultTitleKey)}</h1>

            <p className="max-w-sm text-center leading-relaxed text-gray-500">
              {t(resultTextKey)}
            </p>
          </div>
        )}
      </div>

      {done && (
        <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
          <Form method="post" onSubmit={handleSubmit}>
            <input name={hiddenInputName} type="hidden" value="continue" />
            <Button
              title={buttonKey ? t(buttonKey) : t("shared.continue")}
              type="submit"
            />
          </Form>
        </div>
      )}
    </Layout>
  )
}
